import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { SimpleContent, SimpleContentDescription, UniverseSections } from '../../generated/contentful-generated-types';
import { isSimpleContent } from '../../utils/contentful';
import { useRichText } from '../../hooks/richtext';
import { getColorFromCode } from '../../common/constants';
import EntryLink from '../EntryLink';

interface Props {
  section: UniverseSections;
}

type ExtendedSimpleContent = SimpleContent & {
  simpleContentDescription: SimpleContentDescription;
};

export const HighlightNoImage: FunctionComponent<Props> = ({ section }) => {
  const items = section?.itemsCollection?.items;

  const simpleContentItem = items?.find((item): item is ExtendedSimpleContent => !!item && isSimpleContent(item));

  if (!simpleContentItem) {
    return null;
  }

  const { title, simpleContentDescription: description, buttonLabel, relatedContent, charterTheme } = simpleContentItem;

  return (
    <StyledHighlight charterTheme={charterTheme}>
      <StyledInfos>
        <StyledTitle>{title}</StyledTitle>
        <StyledDesc>{description && useRichText(description)}</StyledDesc>
      </StyledInfos>
      {buttonLabel && <StyledButton entry={relatedContent}>{buttonLabel}</StyledButton>}
    </StyledHighlight>
  );
};

const StyledHighlight = styled.div<{ charterTheme: string }>`
  display: flex;
  padding: 32px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  background: var(--Blue-300, #ecfaff);
  background-color: ${props => getColorFromCode(props.charterTheme, 'transparent')};
  gap: 8px;
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: center;
    gap: 24px;
  }
`;

const StyledInfos = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledTitle = styled.div`
  color: var(--Black-500, #323232);
  font-family: 'StyreneA';
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 36px;
  @media (max-width: 576px) {
    font-size: 20px;
  }
`;

const StyledDesc = styled.div`
  color: var(--Black-500, #323232);
  font-family: 'StyreneA';
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 30px;

  @media (max-width: 576px) {
    font-size: 14px;
  }
`;

const StyledButton = styled(EntryLink)`
  padding: 12px 30px;
  display: block;
  width: fit-content;
  border-radius: 4px;

  box-shadow: 2px 2px 10px 0px rgba(50, 50, 50, 0.2);
  cursor: pointer;
  background: ${getColorFromCode('exa black', '#323232')};
  color: var(--white, #fff);
  font-family: 'Styrene Medium';
  font-size: 14px;

  &:hover {
    background: var(--black-400, #676767);
    color: var(--white, #fff);
    transition: all 0.3s ease-in-out;
  }

  @media (max-width: 576px) {
    width: calc(100% - 32px);
    margin: 0 auto;
    text-align: center;
  }
  @media (max-width: 380px) {
    width: 100%;
    margin: 0 auto;
    text-align: center;
  }
`;
